import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet"; // Import for managing SEO
import {
  FaHandHoldingHeart,
  FaBalanceScale,
  FaChartLine,
} from "react-icons/fa";
import { MdHighQuality, MdOutlineEco, MdLightbulb } from "react-icons/md";
import WhatsAppChatWidget from "./WhatsAppChatWidget";

const About = () => {
  const [activeTab, setActiveTab] = useState("tab1");

  // SEO Meta Data

  // Function to handle the tab change
  const openTab = (tabId) => {
    setActiveTab(tabId);
  };

  // Update document title when component mounts (optional fallback)
  useEffect(() => {
    document.title = pageTitle;
  }, []);
  const pageTitle =
    "About Us - Ardor Biomed | Natural Healing with Nata De Coco & Monolaurin";
  const pageDescription =
    "Ardor Biomed specializes in Nata De Coco wound dressings, alcohol-free sanitizers, burn care, and coconut cosmetics powered by Lauric Acid and monolaurin.";
  const pageUrl = "https://ardorbiomed.com/about";
  const featuredImage = "https://ardorbiomed.com/assets/img/logo-3.png";
  return (
    <>
      <Helmet>
        {/* Primary Meta Tags */}
        <title>{pageTitle}</title>
        <meta name="description" content={pageDescription} />
        <link rel="canonical" href={pageUrl} />

        {/* Open Graph / Facebook */}
        <meta property="og:title" content={pageTitle} />
        <meta property="og:description" content={pageDescription} />
        <meta property="og:type" content="website" />
        <meta property="og:url" content={pageUrl} />
        <meta property="og:image" content={featuredImage} />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        <meta property="og:site_name" content="Ardor Biomed" />
        <meta property="og:locale" content="en_US" />

        {/* Twitter Card */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={pageTitle} />
        <meta name="twitter:description" content={pageDescription} />
        <meta name="twitter:image" content={featuredImage} />
        <meta name="twitter:site" content="@ardorbiomed" />
        <meta name="twitter:creator" content="@ardorbiomed" />

        {/* Schema.org JSON-LD */}
        <script type="application/ld+json">
          {`
          {
            "@context": "https://schema.org",
            "@type": "BlogPosting",
            "headline": "${pageTitle}",
            "description": "${pageDescription}",
            "url": "${pageUrl}",
            "image": "${featuredImage}",
            "author": {
              "@type": "Organization",
              "name": "Ardor Biomed"
            },
            "publisher": {
              "@type": "Organization",
              "name": "Ardor Biomed",
              "logo": {
                "@type": "ImageObject",
                "url": "https://ardorbiomed.com/logo.png"
              }
            },
            "datePublished": "2023-11-15",
            "mainEntityOfPage": {
              "@type": "WebPage",
              "@id": "${pageUrl}"
            }
          }
        `}
        </script>
      </Helmet>

      <div className="banner-section">
        <img
          src="./assets/img/bannerbreadcrumbs.jpg"
          alt="Banner"
          className="img-fluid banner-img"
        />

        <div className="breadcrumb-container">
          <h2>About Us</h2>
          <nav aria-label="breadcrumb">
            <ol className="breadcrumb mb-0">
              <li className="breadcrumb-item">
                <Link to="/">Home</Link>
              </li>

              <li className="breadcrumb-item active" aria-current="page">
                About Us
              </li>
            </ol>
          </nav>
        </div>
      </div>
      <div className="container py-4">
        <div className="row align-items-center">
          {/* Left: Video Section */}
          <div className="col-md-6 mb-4 mb-md-0">
            <video
              src="./assets/img/video.mp4"
              autoPlay
              muted
              loop
              className="w-100 rounded"
            >
              Your browser does not support the video tag.
            </video>
          </div>

          {/* Right: Content Section */}
          <div className="col-md-6">
            <h2 className="text-center">About Us</h2>
            <p className="text-justify">
              Ardor Biomed is a startup company founded with a focus on
              developing lifestyle and health-related products with functional
              ingredients derived through biologic processes. We are DPIIT
              registered and funded by the Government of India's Department of
              Science & Technology and Startup India schemes.
            </p>
          </div>
        </div>
      </div>
      <div>
        <img src="./assets/img/vision.jpg" alt="Ardor Biomed Vision" />
      </div>
      <div className="values-section">
        <h2>Our Values</h2>
        <div className="values-grid">
          <div className="value-item">
            <FaHandHoldingHeart className="value-icon" />
            <span>Integrity</span>
          </div>
          <div className="value-item">
            <FaBalanceScale className="value-icon" />
            <span>Equality</span>
          </div>
          <div className="value-item">
            <FaChartLine className="value-icon" />
            <span>Performance Driven</span>
          </div>
          <div className="value-item">
            <MdHighQuality className="value-icon" />
            <span>Quality First</span>
          </div>
          <div className="value-item">
            <MdOutlineEco className="value-icon" />
            <span>Sustainability</span>
          </div>
          <div className="value-item">
            <MdLightbulb className="value-icon" />
            <span>Think Outside the Box</span>
          </div>
        </div>
      </div>
      <div className="tabs-container">
        <h1 className="text-center p-3">At Ardor Biomed</h1>
        <header className="tab-header">
          {/* Tab Buttons */}
          <div className="tab-buttons-container">
            <button
              type="button"
              onClick={() => openTab("tab1")}
              className={`tab-button ${activeTab === "tab1" ? "active" : ""}`}
            >
              What we do
            </button>
            <button
              type="button"
              onClick={() => openTab("tab2")}
              className={`tab-button ${activeTab === "tab2" ? "active" : ""}`}
            >
              Impacting 8 million views
            </button>
            <button
              type="button"
              onClick={() => openTab("tab5")}
              className={`tab-button ${activeTab === "tab5" ? "active" : ""}`}
            >
              Sustainable Tech at Core of our Products
            </button>
            <button
              type="button"
              onClick={() => openTab("tab3")}
              className={`tab-button ${activeTab === "tab3" ? "active" : ""}`}
            >
              Goodness of Coconut
            </button>
            <button
              type="button"
              onClick={() => openTab("tab4")}
              className={`tab-button ${activeTab === "tab4" ? "active" : ""}`}
            >
              Technology
            </button>
          </div>
        </header>

        {/* Tab Content */}
        <div className="tab-body">
          <section
            id="tab1"
            className={`tab-content ${activeTab === "tab1" ? "active" : ""}`}
          >
            <div className="tab-contentitems">
              <div>
                <img
                  src="./assets/img/abou1.png"
                  alt="What We Do at Ardor Biomed"
                />
              </div>
              <div>
                <h3>What We Do</h3>
                <p>
                  The key to several of our lifestyle related challenges lies in
                  intelligent use of Biologics. For ages, mankind has harnessed
                  nature derived compounds for a healthy lifestyle. With the
                  advent of modern science, the quest to relieve symptom has
                  prevailed rather than seeking a cure. Biological derivatives
                  had taken a backfoot largely until recent times.
                </p>
                <p>
                  We at Ardor Biomed have made it our mandate to bring the
                  goodness of biologics for a healthy and sustainable lifestyle.
                  We are focused on developing innovative products in the
                  Healthcare, Cosmeceutical and nutraceutical domains leveraging
                  technology to make our products relevant to our customers
                </p>
              </div>
            </div>
          </section>

          <section
            id="tab2"
            className={`tab-content ${activeTab === "tab2" ? "active" : ""}`}
          >
            {" "}
            <div className="tab-contentitems">
              <div>
                <img
                  src="./assets/img/about2.png"
                  alt="Impacting 8 Million Views with Coconut Products"
                />
              </div>
              <div>
                <h3>Impacting 8 Million Views</h3>
                <p>
                  The coconut tree is the source of livelihood for more than 8
                  million people in the Southern Indian state of Tamil Nadu.
                  That is as many people there are in Hong Kong!
                </p>
                <p>
                  Coconut tree or Kalpavriksha as it is called because every
                  part of it gives value to mankind. Unfortunately, it is
                  estimated that on an annual basis, about 200 million liters of
                  the water found in matured coconuts go to waste without any
                  value addition during its process in copra yards.
                </p>
                <p>
                  We have established a supply chain for collection of the
                  wasted matured coconut water from the copra processing yards
                  to produce our bio-cellulose products. This has helped the
                  rural community primarily engaged in this industry to add
                  value by supplying this agro-waste.
                </p>
              </div>
            </div>
          </section>
          <section
            id="tab5"
            className={`tab-content ${activeTab === "tab5" ? "active" : ""}`}
          >
            {" "}
            <div className="tab-contentitems">
              <div>
                <img
                  src="./assets/img/about3.jpg"
                  alt="Sustainable Technology at Ardor Biomed"
                />
              </div>
              <div>
                <h3>Sustainable Tech at Core of our Products</h3>
                <p>Microbes! A friend and foe to mankind.</p>
                <p>
                  {" "}
                  They are around and its inevitable to deal with them. Why not
                  make the best out of them. We have gone a long way in
                  isolating a friendly strain of microbe that helps us to
                  convert our coconut water based media to bio-cellulose. Just
                  as microbes convert milk to curd, Wheat to beer, Gherkins to
                  tasty pickles, Our friendly microscopic critter helps us
                  produce our bio-cellulose, which is core to our products.
                </p>
                <p>
                  We have perfected the fermentation process by isolating our
                  own strain of microbe to produce high yields of bio-cellulose,
                  which is totally natural, sustainably produced and have
                  multiple applications in Healthcare, Cosmeceutical and
                  nutraceutical domains.
                </p>
              </div>
            </div>
          </section>
          <section
            id="tab3"
            className={`tab-content ${activeTab === "tab3" ? "active" : ""}`}
          >
            {" "}
            <div className="tab-contentitems">
              <div>
                <img
                  src="./assets/img/about4.jpg"
                  alt="Health Benefits of Coconut at Ardor Biomed"
                />
              </div>
              <div>
                <h3>Goodness of Coconut</h3>
                <p>Who would say no to coconut!</p>
                <p>
                  The rich and creamy flavor of coconut milk shake, the
                  refreshing and thirst quenching coconut water, rejuvenating
                  feel of the coconut oil or just the tropical tipsy coconut
                  beverage. It is relished in every way possible and for a
                  reason.
                </p>
                <p>
                  The wonder compound, lauric acid is a key ingredient in
                  mother's milk and also the humble coconut. Although it
                  constitutes just 7% of mother's milk, lauric acid makes up
                  almost 50% of virgin coconut oil. This compound has
                  scientifically proven to have a wide spectrum anti-microbial
                  properties in its form as monolaurin. It is also known to have
                  anti-inflammatory properties and there are several reports of
                  it being used as supplement for nuro-degenerative conditions
                  such as Alzimer's. The ketones produced during monolaurin
                  consumption helps in witght reduction.
                </p>
                <p>
                  Our studies have shown the skin cell regeneration properties
                  of monolaurin. Consumption of monolaurin helps to fight away
                  infections and maintain skin health.
                </p>
              </div>
            </div>
          </section>

          <section
            id="tab4"
            className={`tab-content ${activeTab === "tab4" ? "active" : ""}`}
          >
            {" "}
            <div className="tab-contentitems">
              <div>
                <img
                  src="./assets/img/about5.jpg"
                  alt="Biotechnology at Ardor Biomed"
                />
              </div>
              <div>
                <h3>Technology</h3>
                <p>
                  Biologics is at the core of our products. With a deep
                  understanding about the benefits of microbes, we employ the
                  latest bio-technological concepts to create our unique
                  products. We emphasize on utilizing natural ingredients in all
                  our products as functional compounds. This is ensured by our
                  technically strong team of experts and scientists working
                  relentlessly to bring the best products to our customers.
                </p>
              </div>
            </div>
          </section>
        </div>
      </div>
      <WhatsAppChatWidget
        phoneNumber="+919488828030"
        companyName="Ardor Biomed"
      />
    </>
  );
};

export default About;
